import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronDown, LogOut, Settings as SettingsIcon } from "lucide-react";
import { api } from "@/lib/api";

export type OrgOption = { id: string; name: string; role: "owner" | "member" };

export function UserMenu({
  user,
  orgs,
  activeOrgId,
  onOrgSwitched,
}: {
  user: { email: string; name: string | null };
  orgs: OrgOption[];
  activeOrgId: string;
  onOrgSwitched?: (orgId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  async function switchOrg(orgId: string) {
    if (orgId === activeOrgId) {
      setOpen(false);
      return;
    }
    const res = await api<{ ok: true; activeOrgId: string }>("/auth/switch-org", {
      method: "POST",
      body: JSON.stringify({ orgId }),
    });
    if (res.ok && res.data) {
      setOpen(false);
      onOrgSwitched?.(res.data.activeOrgId);
      window.location.reload();
    }
  }

  async function signOut() {
    const res = await api<{ ok: true; logoutUrl?: string }>("/auth/logout", {
      method: "POST",
    });
    if (res.data?.logoutUrl) {
      window.location.assign(res.data.logoutUrl);
      return;
    }
    navigate("/", { replace: true });
  }

  const initials = (user.name || user.email)
    .split(/[\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  const activeOrg = orgs.find((o) => o.id === activeOrgId);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white py-0.5 pl-0.5 pr-2 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white dark:bg-slate-100 dark:text-slate-900">
          {initials || "·"}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-800 dark:bg-slate-900">
          <div className="border-b border-slate-100 px-3 py-3 dark:border-slate-800">
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {user.name || user.email}
            </div>
            {user.name && (
              <div className="text-xs text-slate-500 dark:text-slate-400">{user.email}</div>
            )}
          </div>
          {orgs.length > 1 && (
            <div className="border-b border-slate-100 py-1 dark:border-slate-800">
              <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Switch organization
              </div>
              {orgs.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => switchOrg(o.id)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-slate-900 dark:text-slate-100">{o.name}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">{o.role}</span>
                  </span>
                  {o.id === activeOrgId && (
                    <Check className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  )}
                </button>
              ))}
            </div>
          )}
          {orgs.length === 1 && activeOrg && (
            <div className="border-b border-slate-100 px-3 py-2 dark:border-slate-800">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Organization
              </div>
              <div className="mt-0.5 text-sm text-slate-900 dark:text-slate-100">{activeOrg.name}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{activeOrg.role}</div>
            </div>
          )}
          <div className="py-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/dashboard/team");
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <SettingsIcon className="h-4 w-4" />
              Settings
            </button>
            <button
              type="button"
              onClick={signOut}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
